import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { bridgeClubAPI } from '../services/bridgeClubAPI';

interface LinkedStore {
  id: number;
  name: string;
  city: string;
  state: string;
  phone?: string;
  address?: string;
  logo_url?: string | null;
  logo_background_color?: string;
}

export interface BridgeClubMember {
  id: number;
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  member_code: string;
  linked_store: LinkedStore | null;
  loyalty_bags_purchased: number;
  loyalty_bags_redeemed: number;
  loyalty_progress: number;
  bags_until_reward: number;
  loyalty_reward_available: boolean;
  available_rewards: number;
  city: string;
  state: string;
  zip_code: string;
  latitude?: number;
  longitude?: number;
  notifications_enabled: boolean;
  feed_reminders_enabled: boolean;
  weather_alerts_enabled: boolean;
  reminder_days_before: number;
  terms_accepted_at?: string | null;
  privacy_accepted_at?: string | null;
  created_at: string;
  last_login?: string;
  profile_picture_url?: string | null;
  // Local Access (Mill Store features)
  local_access: boolean;
  local_access_method?: 'radius' | 'code' | 'admin';
  local_access_granted_at?: string | null;
  // Computed server-side: True only when local_access=True AND
  // home_location is the Kettle Falls mill. Drives variant selection
  // in WelcomePopup (local mill members see Order Online / locker /
  // $10 credit / BYOC; retail-partner members see Buy 10 Get 1 Free).
  is_local_mill_member?: boolean;
  // Scan & Go
  scan_and_go_tutorial_complete: boolean;
  helcim_customer_id?: string | null;
  // Guided setup (onboarding) gate signals — from getMe.
  onboarding_complete: boolean;
  is_delivery_subscriber: boolean;
  // Where city/state/zip come from: 'delivery' = the delivery subscription's service
  // address (the one source of truth for a subscriber), 'profile' = the member's own.
  location_source?: 'delivery' | 'profile';
  delivery_address?: { street: string; city: string; state: string; postal_code: string } | null;
}

interface RegisterData {
  email: string;
  password?: string;  // Optional - set after email verification
  first_name: string;
  last_name: string;
  phone?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  home_location_id?: number;  // RetailLocation ID from store finder
  linked_store_id?: number;   // Legacy: Customer ID (deprecated)
  partner_loyalty_id?: string;  // Optional loyalty ID at selected store
  terms_accepted: boolean;
  privacy_accepted: boolean;
  is_local_signup?: boolean;  // True for Kettle Falls mill portal signups
}

interface BridgeClubAuthContextType {
  member: BridgeClubMember | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (data: RegisterData) => Promise<void>;
  refreshMember: () => Promise<void>;
  clearError: () => void;
}

const BridgeClubAuthContext = createContext<BridgeClubAuthContextType | undefined>(undefined);

export const BridgeClubAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [member, setMember] = useState<BridgeClubMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAuth = useCallback(async () => {
    // Only check bridge-club auth on relevant routes
    const path = window.location.pathname;
    if (!path.startsWith('/bridge-club') && !path.startsWith('/book')) {
      setIsLoading(false);
      return;
    }
    // Auth is via httpOnly cookies - try to fetch current member
    // Timeout: if auth check takes >5s, bail to login
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Auth check timeout')), 5000)
    );
    try {
      const data = await Promise.race([bridgeClubAPI.getMe(), timeout]);
      setMember(data);
    } catch {
      // Not authenticated, token expired, or timeout
      setMember(null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    setError(null);
    try {
      const data = await bridgeClubAPI.login(email, password);
      // Tokens are set via httpOnly cookies by the backend
      setMember(data.member);
    } catch (err: any) {
      const message = err?.detail || err?.message || 'Login failed';
      setError(message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookies
      await bridgeClubAPI.logout();
    } catch {
      // Ignore errors, still clear local state
    }
    setMember(null);
  };

  const register = async (data: RegisterData) => {
    setError(null);
    try {
      // Registration now sends verification email - user sets password after verification
      await bridgeClubAPI.register(data);
      // No member is returned until email is verified and password is set
    } catch (err: any) {
      const message = err?.detail || err?.message || 'Registration failed';
      setError(message);
      throw err;
    }
  };

  const refreshMember = async () => {
    try {
      const data = await bridgeClubAPI.getMe();
      setMember(data);
    } catch (err) {
      console.error('Failed to refresh member:', err);
    }
  };

  const clearError = () => setError(null);

  return (
    <BridgeClubAuthContext.Provider value={{
      member,
      isAuthenticated: !!member,
      isLoading,
      error,
      login,
      logout,
      register,
      refreshMember,
      clearError,
    }}>
      {children}
    </BridgeClubAuthContext.Provider>
  );
};

export const useBridgeClubAuth = () => {
  const context = useContext(BridgeClubAuthContext);
  if (!context) {
    throw new Error('useBridgeClubAuth must be used within BridgeClubAuthProvider');
  }
  return context;
};
